import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { StorageUtilityLib, TableActionObject, TableColumn, TableColumnType, UserPreferencesService } from '@thehubz/client/core';
@Component({
  selector: 'sp-select-table-column',
  templateUrl: './select-table-column.component.html',
  styleUrls: ['./select-table-column.component.scss'],
})
export class SelectTableColumnComponent implements OnInit {
  tableCols!: TableColumn[];
  stickyTableCols!: TableColumn[];
  additionalTableCols!: TableColumn[];
  localStorageColsState?: TableColumn[];
  sharedTaskStatusColumn?: TableColumn[];
  TableColumnType = TableColumnType;
  allComplete = false;
  resetViewFlag = false;

  constructor(
    public dialogRef: MatDialogRef<SelectTableColumnComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { defaultTableCols: TableColumn[]; additionalColumns: TableColumn[]; sharedTaskStatusColumn?: TableColumn[]; headerType: string },
    private userPreferences: UserPreferencesService,
    private storageUtility: StorageUtilityLib,
  ) {}

  ngOnInit(): void {
    this.tableCols = JSON.parse(JSON.stringify(this.data.defaultTableCols.filter((col) => col.columnDef !== 'actions' && !col.sticky)));
    const defaultColumnsState = this.storageUtility.getLocalStorage<TableColumn[]>('default_columns_state');
    this.localStorageColsState = defaultColumnsState.length ? defaultColumnsState : undefined;
    this.additionalTableCols = JSON.parse(JSON.stringify(this.data.additionalColumns));
    this.sharedTaskStatusColumn = JSON.parse(JSON.stringify(this.data.sharedTaskStatusColumn));
    this.stickyTableCols = this.data.defaultTableCols.filter((col) => col.sticky);
    this.allComplete = this.tableCols !== null && this.tableCols.every((t) => t.visible);
  }

  /**
   * cdkDropListDropped event method
   * @param event
   */
  drop(event: CdkDragDrop<string[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  /**
   * mat-checkbox change event method
   * @param checked
   * @param col
   * @param index
   */
  defaultColChange(checked: boolean, col: TableColumn, index: number) {
    col.visible = checked;
    this.resetViewFlag = false;
    if (checked) {
      // Removes it from the index
      this.tableCols.splice(index, 1);
      // Count the number of active checked column
      const activeCol = this.tableCols.filter((col) => col.visible).length;
      // Insert the selected checkbox at the end of the active checked columns
      this.tableCols.splice(activeCol, 0, col);
    } else {
      if (col.columnType === 'additional') {
        // Removes it from the index
        this.tableCols.splice(index, 1);
        // unchecked item back to additional col array
        this.additionalTableCols.unshift(col);
      } else if (col.columnType === 'shared_task_status' && this.sharedTaskStatusColumn) {
        // Removes it from the index
        this.tableCols.splice(index, 1);
        // unchecked item back to additional col
        this.sharedTaskStatusColumn.unshift(col);
      } else {
        // Removes it from the index
        this.tableCols.splice(index, 1);
        // Push it in the first position
        this.tableCols.push(col);
      }
    }

    this.allComplete = this.tableCols !== null && this.tableCols.every((t) => t.visible);
  }
  /**
   * mat-checkbox change event method
   * @param checked
   * @param col
   * @param index
   */
  addColChange(checked: boolean, col: TableColumn, index: number) {
    col.visible = checked;
    this.resetViewFlag = false;
    if (checked) {
      // Removes it from the index
      this.additionalTableCols.splice(index, 1);
      // Count the number of active checked column
      const activeCol = this.tableCols.filter((col) => col.visible).length;
      // Insert the selected checkbox at the end of the active checked columns
      this.tableCols.splice(activeCol, 0, col);
    }
  }

  addColChangeSharedStatus(checked: boolean, col: TableColumn, index: number) {
    col.visible = checked;
    this.resetViewFlag = false;
    if (checked && this.sharedTaskStatusColumn) {
      // Removes it from the index
      this.sharedTaskStatusColumn.splice(index, 1);
      // Count the number of active checked column
      const activeCol = this.tableCols.filter((col) => col.visible).length;
      // Insert the selected checkbox at the end of the active checked columns
      this.tableCols.splice(activeCol, 0, col);
    }
  }

  someComplete(): boolean {
    if (this.tableCols === null) {
      return false;
    }
    return (this.tableCols.filter((t) => t.visible).length > 0 || !!this.stickyTableCols.length) && !this.allComplete;
  }

  setAll(completed: boolean) {
    this.allComplete = completed;
    if (this.tableCols === null) {
      return;
    }
    this.tableCols.forEach((t) => (t.visible = completed));
  }

  resetView() {
    this.resetViewFlag = true;
    const defaultTableCols = this.localStorageColsState ? this.localStorageColsState : this.data.defaultTableCols;
    this.tableCols = defaultTableCols
      .filter((col) => col.columnDef !== 'actions' && !['additional', 'shared_task_status'].includes(col.columnType || '') && !col.sticky)
      .map((col) => ({ ...col, visible: this.localStorageColsState ? col.visible : true }))
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    this.additionalTableCols = [
      ...this.data.defaultTableCols
        .filter((col) => col.columnDef !== 'actions' && col.columnType === 'additional')
        .map((col) => ({ ...col, visible: false }))
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
      ...JSON.parse(JSON.stringify(this.data.additionalColumns)),
    ];
    this.sharedTaskStatusColumn = [
      ...this.data.defaultTableCols
        .filter((col) => col.columnDef !== 'actions' && col.columnType === 'shared_task_status')
        .map((col) => ({ ...col, visible: false }))
        .sort((a, b) => (a.sequence || 0) - (b.sequence || 0)),
      ...JSON.parse(JSON.stringify(this.data.sharedTaskStatusColumn)),
    ];
    this.allComplete = this.tableCols !== null && this.tableCols.every((t) => t.visible);
  }

  saveView() {
    this.tableCols.unshift(...this.stickyTableCols);
    this.tableCols.push(TableActionObject);
    this.userPreferences.selectedTableColumn.next({
      defaultColumn: this.tableCols,
      additionalColumn: this.additionalTableCols,
      sharedTaskStatusColumn: this.sharedTaskStatusColumn,
      changeType: this.data.headerType,
      resetViewFlag: this.resetViewFlag,
    });
    this.dialogRef.close(true);
  }
}
